// CSV export utility for dashboard data

import type { ChartDataPoint, Performer, DailyChallengeStat, PeriodState, PeriodOption, CustomDateRange, CardKey } from '../types';

interface StatSummary {
  label: string;
  value: string;
  change: string;
}

export interface DashboardExportData {
  dailyActiveUsers: StatSummary;
  vsGames: StatSummary;
  singleGames: StatSummary;
  dailyChallengeMain: { value: string; change: string; successRate: string };
  dailyChallengeStats: DailyChallengeStat[];
  completedVsGames: StatSummary;
  vsBots: StatSummary;
  topPerformers: Performer[];
  chartData: ChartDataPoint[];
  periods: PeriodState;
}

const escapeCsvField = (value: string): string => {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

const buildCsvRow = (fields: string[]): string =>
  fields.map(escapeCsvField).join(',');

const fmt = (d: Date): string =>
  d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const getDateRanges = (period: PeriodOption, customRange?: CustomDateRange): { current: string; previous: string } => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (period === 'custom' && customRange) {
    const start = new Date(customRange.startDate + 'T00:00:00');
    const end = new Date(customRange.endDate + 'T00:00:00');
    const spanMs = end.getTime() - start.getTime();
    let compMs = spanMs;
    if (customRange.comparisonOverride) {
      const { value, unit } = customRange.comparisonOverride;
      const compDays = unit === 'days' ? value : unit === 'weeks' ? value * 7 : value * 30;
      compMs = compDays * 86400000;
    }
    const compPrevEnd = new Date(start.getTime() - 86400000);
    const compPrevStart = new Date(compPrevEnd.getTime() - compMs + 86400000);
    return {
      current: `${fmt(start)} – ${fmt(end)}`,
      previous: `${fmt(compPrevStart)} – ${fmt(compPrevEnd)}`,
    };
  }

  const daysMap: Record<string, number> = { '1D': 1, '7D': 7, '30D': 30, '90D': 90 };
  const days = daysMap[period] || 1;

  const currentEnd = today;
  const currentStart = new Date(today.getTime() - (days - 1) * 86400000);
  const prevEnd = new Date(currentStart.getTime() - 86400000);
  const prevStart = new Date(prevEnd.getTime() - (days - 1) * 86400000);

  if (days === 1) {
    return { current: fmt(currentEnd), previous: fmt(prevEnd) };
  }
  return {
    current: `${fmt(currentStart)} – ${fmt(currentEnd)}`,
    previous: `${fmt(prevStart)} – ${fmt(prevEnd)}`,
  };
};

const buildComparisonNote = (period: PeriodOption, customRange?: CustomDateRange): string => {
  const { current, previous } = getDateRanges(period, customRange);
  return `Current: ${current} | Compared to: ${previous}`;
};

const addStatRow = (
  lines: string[],
  stat: StatSummary,
  period: PeriodOption,
  customRange?: CustomDateRange,
) => {
  const { current, previous } = getDateRanges(period, customRange);
  lines.push(buildCsvRow([stat.label, stat.value, stat.change, current, previous]));
};

export const exportDashboardCsv = (data: DashboardExportData): void => {
  const lines: string[] = [];
  const { periods } = data;

  // Report header
  lines.push('=== SudokuVs Admin Dashboard Report ===');
  lines.push(buildCsvRow(['Exported', new Date().toLocaleString()]));
  lines.push('');

  // Summary stats with date ranges
  lines.push('=== Summary Stats ===');
  lines.push(buildCsvRow(['Metric', 'Value', 'Change', 'Current Period', 'Compared To']));
  addStatRow(lines, data.dailyActiveUsers, periods.dailyActiveUsers, periods.customRanges.dailyActiveUsers);
  addStatRow(lines, data.vsGames, periods.vsGames, periods.customRanges.vsGames);
  addStatRow(lines, data.singleGames, periods.singleGames, periods.customRanges.singleGames);

  // Daily challenge
  const dcRange = getDateRanges(periods.dailyChallenge, periods.customRanges.dailyChallenge);
  lines.push(buildCsvRow(['Daily Challenge', data.dailyChallengeMain.value, data.dailyChallengeMain.change, dcRange.current, dcRange.previous]));
  for (const stat of data.dailyChallengeStats) {
    lines.push(buildCsvRow([`  ${stat.label}`, stat.value, stat.change, '', '']));
  }

  addStatRow(lines, data.completedVsGames, periods.completedVsGames, periods.customRanges.completedVsGames);
  addStatRow(lines, data.vsBots, periods.completedVsBots, periods.customRanges.completedVsBots);
  lines.push('');

  // Top performers
  lines.push(`=== Top Performers (${buildComparisonNote(periods.topPerformers, periods.customRanges.topPerformers)}) ===`);
  lines.push(buildCsvRow(['Mode', 'Player', 'Wins', 'Total Plays', 'Rank']));
  for (const p of data.topPerformers) {
    lines.push(buildCsvRow([p.mode, p.name, String(p.wins ?? ''), String(p.totalPlays ?? ''), p.rankName ?? '']));
  }
  lines.push('');

  // Chart data
  if (data.chartData.length > 0) {
    const chartPeriod = periods.chart as PeriodOption;
    const chartNote = buildComparisonNote(chartPeriod, periods.customRanges.chart);
    lines.push(`=== Game History (${chartNote}) ===`);
    lines.push(buildCsvRow(['Date', 'Daily Challenge', 'VS Random', 'VS Friends', 'VS Bot', 'Single Mode']));
    for (const d of data.chartData) {
      lines.push(buildCsvRow([
        d.date || d.label,
        String(d.daily),
        String(d.vsRandom),
        String(d.vsFriends),
        String(d.vsBot),
        String(d.single),
      ]));
    }
  }

  const csvContent = lines.join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `sudokuvs-dashboard-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
